import { useState, useEffect } from 'react';
import { HotelWithSelection, CompetitorQuote, CompetitorSupplier } from '@/types/hotel-search';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Star, MapPin, ExternalLink, ChevronLeft, ChevronRight,
  Wifi, Check, Tag, Building2, Users, Info, MessageSquare,
  CheckCircle2, AlertCircle, GitCompare, Plus, Trash2, Footprints, Heart,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { COMPETITOR_SUPPLIER_OPTIONS } from '@/lib/hotel-competitor';
import {
  computeGrossPriceSummary, computeGrossForRate,
  formatGrossPrice, multiplierLabel,
} from '@/lib/hotel-tax';

interface HotelDetailModalProps {
  hotel: HotelWithSelection | null;
  open: boolean;
  nights?: number;
  onClose: () => void;
  onToggleChosen: (hotelId: string) => void;
  onToggleFavorite: (hotelId: string) => void;
  onCommentSave: (hotelId: string, comment: string) => void;
  onCompetitorQuoteSave: (hotelId: string, quotes: CompetitorQuote[] | null) => void;
}

type CompRow = { supplier: CompetitorSupplier | ''; price: string; description: string; supplier_url: string };

const blankCompRow = (): CompRow => ({ supplier: '', price: '', description: '', supplier_url: '' });

function quotesToRows(quotes: CompetitorQuote[] | null | undefined): CompRow[] {
  if (!quotes?.length) return [blankCompRow()];
  return quotes.map(q => ({
    supplier: q.supplier,
    price: q.price ?? '',
    description: q.description ?? '',
    supplier_url: q.supplier_url ?? '',
  }));
}

function StarRating({ rating, max = 5 }: { rating: number; max?: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: max }).map((_, i) => (
        <Star key={i} className={cn('w-4 h-4', i < rating ? 'fill-amber-400 text-amber-400' : 'fill-gray-200 text-gray-200')} />
      ))}
    </div>
  );
}

function BookingScore({ score, label }: { score: number | null | undefined; label: string | null | undefined }) {
  if (score == null) return <span className="text-sm text-gray-400">N/A</span>;
  const color =
    score >= 9 ? 'bg-emerald-500' :
    score >= 8 ? 'bg-teal-500' :
    score >= 7 ? 'bg-yellow-500' : 'bg-orange-500';
  return (
    <div className="flex items-center gap-2">
      <span className={cn('text-white text-sm font-bold px-2 py-1 rounded-md', color)}>
        {score.toFixed(1)}
      </span>
      {label && <span className="text-sm font-semibold text-gray-700">{label}</span>}
    </div>
  );
}

export function HotelDetailModal({
  hotel,
  open,
  nights,
  onClose,
  onToggleChosen,
  onToggleFavorite,
  onCommentSave,
  onCompetitorQuoteSave,
}: HotelDetailModalProps) {
  const [imgIndex, setImgIndex] = useState(0);
  const [comment, setComment] = useState('');
  const [commentDirty, setCommentDirty] = useState(false);

  const [compRows, setCompRows] = useState<CompRow[]>([blankCompRow()]);
  const [compDirty, setCompDirty] = useState(false);

  useEffect(() => {
    if (!hotel || !open) return;
    setImgIndex(0);
    setComment(hotel.selection.comments ?? '');
    setCommentDirty(false);
    setCompRows(quotesToRows(hotel.selection.competitor_quote));
    setCompDirty(false);
  }, [
    hotel?.hotel_id,
    open,
    hotel?.selection?.comments,
    JSON.stringify(hotel?.selection?.competitor_quote ?? null),
  ]);

  if (!hotel) return null;

  const images = hotel.images ?? [];
  const totalImages = images.length;

  const handleCommentChange = (val: string) => {
    setComment(val);
    setCommentDirty(true);
  };

  const handleCommentSave = () => {
    onCommentSave(hotel.hotel_id, comment);
    setCommentDirty(false);
  };

  const patchCompRow = (index: number, patch: Partial<CompRow>) => {
    setCompRows(prev => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
    setCompDirty(true);
  };

  const addCompRow = () => {
    setCompRows(prev => [...prev, blankCompRow()]);
    setCompDirty(true);
  };

  const removeCompRow = (index: number) => {
    setCompRows(prev => {
      if (prev.length <= 1) return [blankCompRow()];
      return prev.filter((_, i) => i !== index);
    });
    setCompDirty(true);
  };

  const handleCompetitorSave = () => {
    const nonEmpty = compRows.filter(
      r => !!(r.supplier || r.price.trim() || r.description.trim()),
    );
    if (nonEmpty.length === 0) {
      onCompetitorQuoteSave(hotel.hotel_id, null);
      setCompRows([blankCompRow()]);
      setCompDirty(false);
      return;
    }
    if (nonEmpty.some(r => (r.price.trim() || r.description.trim()) && !r.supplier)) {
      return;
    }
    const quotes: CompetitorQuote[] = nonEmpty
      .filter(r => r.supplier)
      .map(r => {
        const quote: CompetitorQuote = {
          supplier: r.supplier as CompetitorSupplier,
          price: r.price.trim(),
          description: r.description.trim(),
        };
        const url = r.supplier_url.trim();
        if (url) quote.supplier_url = url;
        return quote;
      });
    onCompetitorQuoteSave(hotel.hotel_id, quotes.length ? quotes : null);
    setCompDirty(false);
  };

  const handleCompetitorClear = () => {
    setCompRows([blankCompRow()]);
    onCompetitorQuoteSave(hotel.hotel_id, null);
    setCompDirty(false);
  };

  const competitorSaveBlocked = (() => {
    const nonEmpty = compRows.filter(
      r => !!(r.supplier || r.price.trim() || r.description.trim()),
    );
    return nonEmpty.some(r => (r.price.trim() || r.description.trim()) && !r.supplier);
  })();

  const hasSavedCompetitorQuotes = !!hotel.selection.competitor_quote?.length;
  const anyCompRowFilled = compRows.some(
    r => !!(r.supplier || r.price.trim() || r.description.trim()),
  );

  const currentStatus = hotel.selection.status;
  const isFavorite = hotel.selection.is_favorite === true;

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto p-0 gap-0">

        {/* Image gallery */}
        <div className="relative bg-gray-900 rounded-t-lg overflow-hidden" style={{ height: 300 }}>
          {totalImages > 0 ? (
            <>
              <img
                src={images[imgIndex]}
                alt={hotel.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              {totalImages > 1 && (
                <>
                  <button
                    onClick={() => setImgIndex(i => (i - 1 + totalImages) % totalImages)}
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center text-white transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setImgIndex(i => (i + 1) % totalImages)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center text-white transition-colors"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                  <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5">
                    {images.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setImgIndex(i)}
                        className={cn(
                          'rounded-full transition-all',
                          i === imgIndex ? 'bg-white w-4 h-2' : 'bg-white/50 w-2 h-2',
                        )}
                      />
                    ))}
                  </div>
                  <div className="absolute top-3 right-3 bg-black/50 rounded-full px-2 py-1 text-white text-xs">
                    {imgIndex + 1} / {totalImages}
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-500">
              <Building2 className="w-16 h-16" />
            </div>
          )}
        </div>

        <div className="p-6 space-y-6">

          {/* Header: name, stars, availability, status actions */}
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <DialogHeader>
                <DialogTitle className="text-xl font-bold text-gray-900 leading-tight">
                  {hotel.name}
                </DialogTitle>
              </DialogHeader>
              <div className="flex items-center gap-2 mt-1.5">
                <StarRating rating={hotel.classification?.stars ?? 0} />
                {hotel.classification?.stars != null && (
                  <span className="text-sm text-gray-400">{hotel.classification.stars} stars</span>
                )}
                {!hotel.availability && (
                  <Badge variant="outline" className="text-xs text-orange-600 border-orange-200 bg-orange-50">
                    <AlertCircle className="w-3 h-3 mr-1" />
                    Indisponível
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-1 mt-1 text-sm text-gray-500">
                <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                <span>{hotel.location.address}, {hotel.location.city}</span>
              </div>
              {(() => {
                const walk = hotel.walking_distance;
                const walkRef = walk?.reference?.trim();
                const walkMin = walk?.minutes;
                const show = walk != null && (Number.isFinite(walkMin as number) || Boolean(walkRef));
                if (!show) return null;
                return (
                  <div className="flex items-start gap-1.5 mt-2 text-sm text-gray-600">
                    <Footprints className="w-4 h-4 text-voah-teal flex-shrink-0 mt-0.5" />
                    <span>
                      {Number.isFinite(walkMin as number) ? (
                        <span className="font-medium text-gray-800">
                          {Math.round(walkMin as number)} min a pé
                        </span>
                      ) : null}
                      {Number.isFinite(walkMin as number) && walkRef ? <> · </> : null}
                      {walkRef ? (
                        <span className={cn('text-gray-600', !Number.isFinite(walkMin as number) && 'font-medium text-gray-800')}>
                          {walkRef}
                        </span>
                      ) : null}
                    </span>
                  </div>
                );
              })()}
            </div>

            <div className="flex flex-col items-end gap-2 shrink-0 max-w-[200px]">
              <Button
                type="button"
                variant={currentStatus === 'chosen' ? 'default' : 'outline'}
                className={cn(
                  'h-auto py-2 px-3 flex flex-col items-stretch gap-0.5 w-full',
                  currentStatus === 'chosen' && 'bg-voah-teal hover:bg-voah-teal-dark text-white border-voah-teal',
                )}
                onClick={() => onToggleChosen(hotel.hotel_id)}
              >
                <span className="flex items-center justify-center gap-1.5 text-xs font-semibold">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  {currentStatus === 'chosen' ? 'Escolhido na viagem' : 'Marcar como escolhido'}
                </span>
                <span className="text-[10px] font-normal opacity-90 text-center leading-tight">
                  {currentStatus === 'chosen' ? 'Clique para desmarcar' : 'Salva na seleção'}
                </span>
              </Button>
              <Button
                type="button"
                variant={isFavorite ? 'default' : 'outline'}
                className={cn(
                  'h-auto py-2 px-3 flex flex-col items-stretch gap-0.5 w-full',
                  isFavorite && 'bg-rose-500 hover:bg-rose-600 text-white border-rose-500',
                )}
                onClick={() => onToggleFavorite(hotel.hotel_id)}
              >
                <span className="flex items-center justify-center gap-1.5 text-xs font-semibold">
                  <Heart className={cn('w-3.5 h-3.5', isFavorite && 'fill-current')} />
                  {isFavorite ? 'Nos favoritos' : 'Favoritar'}
                </span>
                <span className="text-[10px] font-normal opacity-90 text-center leading-tight">
                  {isFavorite ? 'Clique para remover' : 'Marca para revisar depois'}
                </span>
              </Button>
            </div>
          </div>

          {/* Ratings */}
          <div className="flex items-center gap-6 flex-wrap">
            <div className="space-y-1">
              <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">Booking.com</p>
              <BookingScore score={hotel.rating?.score} label={hotel.rating?.label} />
              <p className="text-xs text-gray-400">{hotel.rating?.review_count?.toLocaleString() ?? '—'} avaliações</p>
            </div>
            {hotel.google_reviews && (
              <div className="space-y-1">
                <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">Google</p>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1">
                    <StarRating rating={Math.round(hotel.google_reviews.rating)} />
                    <span className="text-sm font-semibold text-gray-700">{hotel.google_reviews.rating}</span>
                  </div>
                </div>
                <p className="text-xs text-gray-400">{hotel.google_reviews.review_count?.toLocaleString() ?? '—'} avaliações</p>
              </div>
            )}
            <div className="space-y-1">
              <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">
                Preços{hotel.price_summary ? ` (${hotel.price_summary.currency})` : ''}
              </p>
              {(() => {
                const currency = hotel.price_summary?.currency ?? 'BRL';
                const gross = hotel.rooms?.length
                  ? computeGrossPriceSummary(hotel.rooms, currency)
                  : null;
                if (!gross) {
                  return <p className="text-sm text-gray-400 italic">Indisponível para as datas selecionadas</p>;
                }
                const taxDisplay = gross.hasTaxes ? Math.round(gross.taxMin) : null;
                return (
                  <>
                    <p className="text-base font-bold text-gray-900">
                      {formatGrossPrice(gross.min)}
                      {Math.round(gross.min) !== Math.round(gross.max) && (
                        <> – {formatGrossPrice(gross.max)}</>
                      )}
                      <span className="text-sm font-normal text-gray-400">
                        {' '}total{nights && nights > 0 ? ` · ${nights} noites` : ''}
                      </span>
                      {taxDisplay != null && taxDisplay > 0 && (
                        <span className="text-sm font-normal text-orange-500 ml-1">
                          ({currency} {formatGrossPrice(taxDisplay)} impostos já incluídos)
                        </span>
                      )}
                    </p>
                  </>
                );
              })()}
            </div>
          </div>

          <Separator />

          {/* Description */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
              <Info className="w-4 h-4 text-gray-400" />
              Descrição
            </h4>
            <p className="text-sm text-gray-600 leading-relaxed">{hotel.description}</p>
          </div>

          {/* Keywords */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
              <Tag className="w-4 h-4 text-gray-400" />
              Palavras-chave
            </h4>
            <div className="flex flex-wrap gap-1.5">
              {hotel.keywords.map(kw => (
                <Badge key={kw} variant="secondary" className="text-xs bg-gray-100 text-gray-600 rounded-md">
                  {kw}
                </Badge>
              ))}
            </div>
          </div>

          <Separator />

          {/* Facilities */}
          {hotel.facilities?.most_popular?.length ? (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-1.5">
                <Wifi className="w-4 h-4 text-gray-400" />
                Facilidades em destaque
              </h4>
              <div className="grid grid-cols-2 gap-1.5">
                {hotel.facilities.most_popular.map(f => (
                  <div key={f} className="flex items-center gap-2 text-sm text-gray-600">
                    <Check className="w-3.5 h-3.5 text-voah-teal flex-shrink-0" />
                    <span>{f}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          <Separator />

          {/* Rooms & Rates */}
          {hotel.rooms?.length ? (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-1.5">
                <Users className="w-4 h-4 text-gray-400" />
                Quartos disponíveis
              </h4>
              <div className="space-y-3">
                {hotel.rooms.map(room => (
                  <div key={room.name} className="border border-gray-100 rounded-lg overflow-hidden">
                    <div className="bg-gray-50 px-3 py-2 flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-800">{room.name}</span>
                      {room.max_persons && (
                        <span className="text-xs text-gray-400">
                          <Users className="w-3 h-3 inline mr-1" />
                          máx. {room.max_persons} hóspedes
                        </span>
                      )}
                    </div>
                    <div className="divide-y divide-gray-50">
                      {room.rate_options.map(rate => (
                        <div key={rate.block_id} className="px-3 py-2.5">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1">
                              <div className="flex flex-wrap gap-1 mb-1.5">
                                {rate.rate_options.map(opt => (
                                  <Badge key={opt} variant="outline" className="text-xs px-1.5 py-0 border-gray-200 text-gray-600 rounded-md">
                                    {opt}
                                  </Badge>
                                ))}
                              </div>
                              {rate.discount_type && (
                                <span className="text-xs text-emerald-600 font-medium">
                                  {rate.discount_type}
                                  {rate.discount && ` · ${rate.discount}`}
                                </span>
                              )}
                              <p className="text-xs text-gray-400 mt-1">{rate.taxes_and_charges}</p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              {rate.original_price && (
                                <p className="text-xs text-gray-400 line-through">{rate.original_price}</p>
                              )}
                              <p className="text-sm font-bold text-gray-900">{rate.price}</p>
                              {rate.per_night && (
                                <p className="text-xs text-gray-400">{rate.per_night}/noite</p>
                              )}
                              {(() => {
                                const g = computeGrossForRate(rate);
                                if (!g) return null;
                                return (
                                  <p className="text-xs text-orange-500 font-medium mt-0.5">
                                    c/ imp.: {formatGrossPrice(g.gross)}
                                    <span className="text-orange-400/70 ml-1 font-normal">
                                      ({multiplierLabel(g.multiplier)})
                                    </span>
                                  </p>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          <Separator />

          {/* External links */}
          <div className="flex items-center gap-3">
            <a
              href={hotel.booking_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              Ver no Booking.com
            </a>
            <Separator orientation="vertical" className="h-4" />
            <a
              href={hotel.google_maps_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-voah-teal hover:text-voah-teal-dark font-medium transition-colors"
            >
              <MapPin className="w-4 h-4" />
              Ver no Google Maps
            </a>
          </div>

          <Separator />

          {/* Comparação com outros fornecedores */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-1.5">
              <GitCompare className="w-4 h-4 text-gray-400" />
              Outros fornecedores (comparar com Booking.com)
            </h4>
            <div className="space-y-3 rounded-lg border border-gray-100 bg-gray-50/60 p-3">
              {compRows.map((row, index) => (
                <div
                  key={index}
                  className="relative space-y-3 rounded-md border border-gray-200/80 bg-white/90 p-3 shadow-sm"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                      Cotação {index + 1}
                    </span>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 text-gray-400 hover:text-red-600"
                      onClick={() => removeCompRow(index)}
                      aria-label="Remover cotação"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-gray-500">Fornecedor</Label>
                    <Select
                      value={row.supplier || undefined}
                      onValueChange={v => patchCompRow(index, { supplier: v as CompetitorSupplier })}
                    >
                      <SelectTrigger className="text-sm bg-white">
                        <SelectValue placeholder="Selecione o fornecedor" />
                      </SelectTrigger>
                      <SelectContent>
                        {COMPETITOR_SUPPLIER_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value} className="text-sm">
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-gray-500">Preço</Label>
                    <Input
                      placeholder="Ex.: R$ 4.200, USD 890 / noite..."
                      value={row.price}
                      onChange={e => patchCompRow(index, { price: e.target.value })}
                      className="text-sm bg-white"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-gray-500">URL no fornecedor</Label>
                    <Input
                      type="url"
                      placeholder="https://..."
                      value={row.supplier_url}
                      onChange={e => patchCompRow(index, { supplier_url: e.target.value })}
                      className="text-sm bg-white"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-gray-500">Descrição / observações</Label>
                    <Textarea
                      placeholder="Detalhes da cotação, regime, política de cancelamento..."
                      value={row.description}
                      onChange={e => patchCompRow(index, { description: e.target.value })}
                      className="text-sm resize-none min-h-[72px] bg-white"
                      rows={3}
                    />
                  </div>
                </div>
              ))}

              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full border-dashed text-gray-600"
                onClick={addCompRow}
              >
                <Plus className="w-3.5 h-3.5 mr-1.5" />
                Incluir outro preço
              </Button>

              <div className="flex flex-wrap justify-end gap-2 pt-1">
                {(hasSavedCompetitorQuotes || anyCompRowFilled) && (
                  <Button size="sm" variant="ghost" type="button" className="text-gray-500" onClick={handleCompetitorClear}>
                    Limpar tudo
                  </Button>
                )}
                {compDirty && (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      type="button"
                      onClick={() => {
                        setCompRows(quotesToRows(hotel.selection.competitor_quote));
                        setCompDirty(false);
                      }}
                    >
                      Cancelar
                    </Button>
                    <Button
                      size="sm"
                      type="button"
                      className="bg-voah-teal hover:bg-voah-teal-dark text-white"
                      disabled={competitorSaveBlocked}
                      onClick={handleCompetitorSave}
                    >
                      Salvar cotações
                    </Button>
                  </>
                )}
              </div>
              {competitorSaveBlocked && compDirty && (
                <p className="text-xs text-amber-600">
                  Em cada linha com preço ou descrição, selecione o fornecedor para salvar.
                </p>
              )}
            </div>
          </div>

          <Separator />

          {/* Comment */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
              <MessageSquare className="w-4 h-4 text-gray-400" />
              Comentários
            </h4>
            <Textarea
              placeholder="Anotações pessoais sobre este hotel (visível apenas para você)..."
              value={comment}
              onChange={e => handleCommentChange(e.target.value)}
              className="text-sm resize-none min-h-[80px]"
              rows={3}
            />
            {commentDirty && (
              <div className="flex justify-end mt-2 gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => { setComment(hotel.selection.comments ?? ''); setCommentDirty(false); }}
                >
                  Cancelar
                </Button>
                <Button
                  size="sm"
                  className="bg-voah-teal hover:bg-voah-teal-dark text-white"
                  onClick={handleCommentSave}
                >
                  Salvar comentário
                </Button>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
